import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Lock, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const LIVON_LOGO = "https://static.prod-images.emergentagent.com/jobs/b293bd06-5db6-47e3-9bac-b35b794055ae/images/24e9f4cae61d982d9c454efb36258018ae47a7c7ef1d43817c3543666c110b4a.png";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setError('Ingen återställningslänk hittades');
      return;
    }

    // Verify token
    const verifyToken = async () => {
      try {
        await axios.get(`${API}/api/auth/verify-reset-token/${token}`);
        setTokenValid(true);
      } catch (err) {
        setError(err.response?.data?.detail || 'Länken är ogiltig eller har gått ut');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Lösenordet måste vara minst 6 tecken');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Lösenorden matchar inte');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Lösenordet har återställts!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte återställa lösenordet');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-4" />
          <p className="text-muted-foreground">Verifierar länk...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 flex flex-col">
        <header className="p-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/auth')}
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Tillbaka
          </Button>
        </header>
        
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="bg-card rounded-3xl p-8 shadow-xl border border-border/50 text-center max-w-md">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-destructive" />
            </div>
            
            <h1 className="font-heading text-2xl font-light mb-4">Ogiltig länk</h1>
            <p className="text-muted-foreground mb-6">{error}</p>
            
            <Button onClick={() => navigate('/forgot-password')} className="rounded-full">
              Begär ny återställningslänk
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 flex items-center justify-center px-6">
        <div className="bg-card rounded-3xl p-8 shadow-xl border border-border/50 text-center max-w-md animate-fade-in">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-primary" />
          </div>
          
          <h1 className="font-heading text-2xl font-light mb-4">Lösenordet är återställt!</h1>
          <p className="text-muted-foreground mb-6">
            Du kan nu logga in med ditt nya lösenord.
          </p>
          
          <Button onClick={() => navigate('/auth')} className="rounded-full w-full">
            Logga in
          </Button>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 flex flex-col">
      <header className="p-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/auth')}
          className="rounded-full"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Tillbaka
        </Button>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-3xl p-8 shadow-xl border border-border/50 animate-fade-in">
            <div className="text-center mb-8">
              <img 
                src={LIVON_LOGO} 
                alt="Livon" 
                className="w-16 h-16 mx-auto mb-4"
              />
              <h1 className="font-heading text-2xl font-light">Välj nytt lösenord</h1>
              <p className="text-muted-foreground mt-2">
                Ange ditt nya lösenord nedan.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="password">Nytt lösenord</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minst 6 tecken"
                    className="pl-10 rounded-xl bg-secondary/50 border-0 h-12"
                    data-testid="password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Bekräfta lösenord</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Upprepa lösenordet"
                    className="pl-10 rounded-xl bg-secondary/50 border-0 h-12"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-xl text-base font-medium"
                disabled={loading}
                data-testid="submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Lock className="w-4 h-4 mr-2" />
                )}
                Återställ lösenord
              </Button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
